import React, { useEffect, useLayoutEffect } from "react";
import { Textfield } from "@natera/form";
import classNames from "classnames";
import { HeapPHIDecorator } from "@app/components/analytics";
import { defineMessages, useIntl } from "react-intl";
import {
  SetValidationError,
  ClearValidationError,
} from "@natera/platform/lib/hooks/useError";
import { getMaxDayForDob, MonthsMapOption } from "./months";
import { NoScrollFormField } from "@app/components/ui/inputs/noScrollFormField";

const messages = defineMessages({
  dateOfBirthDayLabel: {
    id: "dateOfBirthVerificationDayLabel",
    defaultMessage: "Day",
  },
  dateOfBirthVerificationInvalidValue: {
    id: "dateOfBirthVerificationInvalidValue",
    defaultMessage: "Invalid or incomplete value.",
  },
});

interface DobDayFieldProps {
  dayInputValue: string;
  setDayInputValue: (value: string) => void;
  selectedMonth?: MonthsMapOption | null;
  selectedYear?: string;
  setSelectedDay: (day: number | undefined) => void;
  showLabel?: boolean;
  disabled?: boolean;

  getValidationError: (field: string) => string | undefined;
  setValidationError: SetValidationError;
  clearValidationError: ClearValidationError;
}

const DobDayField: React.FC<DobDayFieldProps> = ({
  selectedMonth,
  selectedYear,

  dayInputValue,
  setDayInputValue,

  getValidationError,
  setValidationError,
  clearValidationError,
  setSelectedDay,
  showLabel = true,
  disabled,
}) => {
  const intl = useIntl();

  const invalidDayMessage = intl.formatMessage(
    messages.dateOfBirthVerificationInvalidValue
  );

  const numericYear =
    selectedYear && selectedYear.length === 4
      ? Number(selectedYear)
      : undefined;

  const maxDay = selectedMonth
    ? getMaxDayForDob(selectedMonth.value, numericYear)
    : 31;

  const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value.replace(/\D/g, "");
    if (inputValue.length > 2) {
      inputValue = inputValue.slice(0, 2);
    }

    setDayInputValue(inputValue);

    if (
      inputValue === "00" ||
      (inputValue.length === 2 && parseInt(inputValue, 10) > maxDay)
    ) {
      setValidationError("dateOfBirthDay", invalidDayMessage);
    } else {
      clearValidationError("dateOfBirthDay");
      clearValidationError("dateOfBirthIncorrect");
      clearValidationError("dateOfBirthRequired");
    }
  };

  const handleDayBlur = () => {
    if (!dayInputValue) {
      setSelectedDay(undefined);
      return;
    }

    const dayValue = parseInt(dayInputValue, 10);
    if (isNaN(dayValue) || dayValue < 1 || dayValue > maxDay) {
      setSelectedDay(undefined);
      if (getValidationError("dateOfBirthDay") !== invalidDayMessage) {
        setValidationError("dateOfBirthDay", invalidDayMessage);
      }
      return;
    }

    setSelectedDay(dayValue);
    setDayInputValue(dayValue.toString().padStart(2, "0"));
    clearValidationError("dateOfBirthDay");
    clearValidationError("dateOfBirthIncorrect");
  };

  const dayError = getValidationError("dateOfBirthDay");

  useEffect(() => {
    if (dayError && dayError !== invalidDayMessage) {
      setValidationError("dateOfBirthDay", invalidDayMessage);
    }
  }, [dayError, invalidDayMessage, setValidationError]);

  useEffect(() => {
    if (!selectedMonth) {
      setSelectedDay(undefined);
      setDayInputValue("");
      clearValidationError("dateOfBirthDay");
      clearValidationError("dateOfBirthIncorrect");
    }
  }, [selectedMonth]);

  useLayoutEffect(() => {
    const dayValue = parseInt(dayInputValue, 10);
    if (!selectedMonth) return;
    if (!dayInputValue) {
      setSelectedDay(undefined);
      return;
    }
    if (isNaN(dayValue) || dayValue < 1 || dayValue === 0) {
      setSelectedDay(undefined);
      return;
    }
    if (dayValue > maxDay) {
      setSelectedDay(undefined);
      setValidationError("dateOfBirthDay", invalidDayMessage);
    } else {
      setSelectedDay(dayValue);
      clearValidationError("dateOfBirthDay");
    }
  }, [selectedMonth, selectedYear, maxDay, dayInputValue]);

  return (
    <NoScrollFormField
      label={
        showLabel ? intl.formatMessage(messages.dateOfBirthDayLabel) : undefined
      }
      error={getValidationError("dateOfBirthDay")}
      htmlFor="dateOfBirthDay"
      className={classNames("dob-input__day", {
        "dob-verification-form__invalid":
          getValidationError("dateOfBirthIncorrect") ||
          getValidationError("dateOfBirthRequired"),
      })}
    >
      <HeapPHIDecorator protectAttr={["value"]} noWrap={true}>
        <Textfield
          placeholder={intl.formatMessage(messages.dateOfBirthDayLabel)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          id="dateOfBirthDay"
          className="dob-input__input"
          maxLength={2}
          name="dateOfBirthDay"
          data-testid="dateOfBirthDay"
          outline
          value={dayInputValue}
          onChange={handleDayChange}
          onBlur={handleDayBlur}
          fullWidth
          disabled={disabled}
        />
      </HeapPHIDecorator>
    </NoScrollFormField>
  );
};

export default DobDayField;
